import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Video, RefreshCw, Loader2, Trash2, Search, Link2, Music, FileText,
  ChevronLeft, ChevronRight, HardDrive, Calendar, MessageSquare, Play, Wand2,
  ArrowUpDown,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

export default function AdminRecordingsPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterMapped, setFilterMapped] = useState('all');
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [sortBy, setSortBy] = useState('recording_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [availableYears, setAvailableYears] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [selectedWebinarId, setSelectedWebinarId] = useState('');
  const [webinarSearch, setWebinarSearch] = useState('');

  const filteredWebinars = webinars.filter(w =>
    (w.title || '').toLowerCase().includes(webinarSearch.toLowerCase()) ||
    (w.webinar_code || '').toLowerCase().includes(webinarSearch.toLowerCase())
  );
  const perPage = 20;

  const [autoMapping, setAutoMapping] = useState(false);
  const [mapping, setMapping] = useState(false);

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (filterMapped !== 'all') params.mapped = filterMapped;
      if (filterYear) params.year = filterYear;
      if (filterMonth) params.month = filterMonth;
      const response = await adminAPI.get('/admin/recordings', { params });
      setRecordings(response.data.recordings || []);
      setTotal(response.data.total || 0);
      if (response.data.available_years) setAvailableYears(response.data.available_years);
    } catch (error) {
      console.error('Failed to fetch recordings:', error);
      toast.error('Failed to load recordings');
    }
    setLoading(false);
  }, [page, search, filterMapped, filterYear, filterMonth, sortBy, sortOrder]);

  const fetchWebinars = async () => {
    try {
      const response = await adminAPI.get('/admin/webinars', { params: { per_page: 100 } });
      const sorted = (response.data.webinars || []).sort((a, b) =>
        (a.title || '').localeCompare(b.title || '')
      );
      setWebinars(sorted);
    } catch (error) {
      console.error('Failed to fetch webinars:', error);
    }
  };

  useEffect(() => { fetchRecordings(); }, [fetchRecordings]);
  useEffect(() => { fetchWebinars(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await adminAPI.post('/admin/recordings/sync');
      const d = response.data;
      toast.success(`Sync complete: ${d.new} new, ${d.updated} updated, ${d.total_sessions} total sessions`);
      fetchRecordings();
    } catch (error) {
      toast.error('Failed to sync recordings');
    }
    setSyncing(false);
  };

  const handleMapWebinar = async () => {
    if (!selectedRecording || !selectedWebinarId) return;
    setMapping(true);
    try {
      await adminAPI.post(`/admin/recordings/${selectedRecording.id}/map-webinar`, {
        webinar_id: selectedWebinarId,
      });
      toast.success('Recording mapped to webinar');
      setMapModalOpen(false);
      setSelectedRecording(null);
      setSelectedWebinarId('');
      fetchRecordings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to map recording');
    }
    setMapping(false);
  };

  const handleAutoMap = async () => {
    setAutoMapping(true);
    try {
      const response = await adminAPI.post('/admin/recordings/auto-map');
      const d = response.data;
      toast.success(`Auto-mapped ${d.mapped} recordings. ${d.failed} unmatched.`);
      fetchRecordings();
    } catch (error) {
      toast.error('Failed to auto-map recordings');
    }
    setAutoMapping(false);
  };

  const handlePreview = async (recording) => {
    try {
      const response = await adminAPI.get(`/admin/recordings/${recording.id}/preview`);
      const data = response.data;
      if (data.video_url) {
        window.open(data.video_url, '_blank');
        toast.success('Video preview opened in new tab');
      } else if (data.audio_url) {
        window.open(data.audio_url, '_blank');
        toast.success('Audio preview opened in new tab');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview link');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this recording from database? (R2 files will not be deleted)')) return;
    try {
      await adminAPI.delete(`/admin/recordings/${id}`);
      toast.success('Recording removed');
      fetchRecordings();
    } catch (error) {
      toast.error('Failed to delete recording');
    }
  };

  const openMapModal = (recording) => {
    setSelectedRecording(recording);
    setSelectedWebinarId(recording.webinar_id || '');
    setMapModalOpen(true);
  };

  const totalPages = Math.ceil(total / perPage);
  const mappedCount = recordings.filter(r => r.webinar_id).length;
  const unmappedCount = recordings.filter(r => !r.webinar_id).length;

  return (
    <div className="space-y-6" data-testid="admin-recordings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Recording Management</h2>
          <p className="text-slate-500">Synced from R2 (zoom-recordings). Map recordings to webinars for customer access.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchRecordings} variant="outline" size="sm" data-testid="refresh-recordings-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleAutoMap} variant="outline" size="sm" disabled={autoMapping}
            data-testid="auto-map-recordings-btn" className="border-green-500 text-green-700 hover:bg-green-50">
            {autoMapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Auto-Map to Webinars
          </Button>
          <Button onClick={handleSync} size="sm" disabled={syncing} data-testid="sync-recordings-btn"
            className="bg-navy-600 hover:bg-navy-700">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HardDrive className="w-4 h-4 mr-2" />}
            Sync from R2
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Recordings</p>
                <p className="text-3xl font-bold text-navy-700">{total}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Video className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Mapped to Webinar</p>
                <p className="text-3xl font-bold text-green-600">{mappedCount}</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Link2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unmapped</p>
                <p className="text-3xl font-bold text-orange-600">{unmappedCount}</p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">With Transcript</p>
                <p className="text-3xl font-bold text-purple-600">
                  {recordings.filter(r => r.transcript_path).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row gap-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by title, webinar code..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                  data-testid="search-recordings-input"
                />
              </div>
              <Select value={filterMapped} onValueChange={(v) => { setFilterMapped(v); setPage(1); }}>
                <SelectTrigger className="w-40" data-testid="filter-mapped-select">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Mapped</SelectItem>
                  <SelectItem value="false">Unmapped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              {/* Year Filter */}
              <Select value={filterYear} onValueChange={(v) => { setFilterYear(v === 'all' ? '' : v); setFilterMonth(''); setPage(1); }}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Month Filter (only when year selected) */}
              {filterYear && (
                <Select value={filterMonth} onValueChange={(v) => { setFilterMonth(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                      <SelectItem key={m} value={m}>
                        {new Date(2024, parseInt(m)-1).toLocaleString('default', {month: 'long'})}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* Sort */}
              <div className="flex items-center gap-2 ml-auto">
                <ArrowUpDown className="w-4 h-4 text-slate-400" />
                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recording_date">Date</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-9 px-3"
                  onClick={() => { setSortOrder(s => s === 'desc' ? 'asc' : 'desc'); setPage(1); }}>
                  {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recordings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-navy-500" />
            Recordings ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : recordings.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">No recordings found</p>
              <Button onClick={handleSync} size="sm">
                <HardDrive className="w-4 h-4 mr-2" />
                Sync from R2
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[250px]">Title</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Files</TableHead>
                      <TableHead>Video Size</TableHead>
                      <TableHead>Mapped Webinar</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recordings.map((rec) => (
                      <TableRow key={rec.id} data-testid={`recording-row-${rec.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Video className="w-5 h-5 text-slate-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate max-w-[220px]" title={rec.title}>
                                {rec.title}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {rec.webinar_code ? (
                            <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                              {rec.webinar_code}
                            </code>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Calendar className="w-3 h-3" />
                            {rec.recording_date || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1.5">
                            {rec.video_path && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Video className="w-3 h-3" /> MP4
                              </Badge>
                            )}
                            {rec.audio_path && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Music className="w-3 h-3" /> M4A
                              </Badge>
                            )}
                            {rec.transcript_path && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <FileText className="w-3 h-3" /> VTT
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {formatSize(rec.video_size)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {rec.webinar_title ? (
                            <Badge className="bg-green-100 text-green-700 text-xs max-w-[180px] truncate block">
                              {rec.webinar_title}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                              Not Mapped
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(rec)}
                              data-testid={`preview-recording-btn-${rec.id}`}
                              title="Preview / Test Link"
                            >
                              <Play className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openMapModal(rec)}
                              data-testid={`map-recording-btn-${rec.id}`}
                            >
                              <Link2 className="w-3.5 h-3.5 mr-1" />
                              Map
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(rec.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Map to Webinar Modal */}
      <Dialog open={mapModalOpen} onOpenChange={(open) => { setMapModalOpen(open); if (!open) setWebinarSearch(''); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Map Recording to Webinar</DialogTitle>
          </DialogHeader>
          {selectedRecording && (
            <div className="flex flex-col gap-4 py-2 min-h-0 flex-1">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="font-medium text-sm text-navy-700">{selectedRecording.title}</p>
                <p className="text-xs text-slate-500 mt-1">Date: {selectedRecording.recording_date || '—'}</p>
              </div>
              <div className="flex flex-col gap-2 min-h-0 flex-1">
                <label className="text-sm font-medium">Select Webinar</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search webinars by title or code..."
                    value={webinarSearch}
                    onChange={(e) => setWebinarSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-webinar-for-mapping"
                  />
                </div>
                <div className="border rounded-lg overflow-y-auto max-h-[300px] min-h-[200px]">
                  {filteredWebinars.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No webinars found</p>
                  ) : (
                    filteredWebinars.map((w) => (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => setSelectedWebinarId(w.id)}
                        className={`w-full text-left px-4 py-2.5 text-sm border-b last:border-b-0 hover:bg-blue-50 transition-colors ${
                          selectedWebinarId === w.id ? 'bg-blue-100 font-medium text-blue-800' : 'text-slate-700'
                        }`}
                        data-testid={`webinar-option-${w.id}`}
                      >
                        <span className="text-xs text-slate-400 mr-2">{w.webinar_code || ''}</span>
                        {w.title}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="pt-2 border-t">
            <Button variant="outline" onClick={() => { setMapModalOpen(false); setWebinarSearch(''); }}>Cancel</Button>
            <Button onClick={handleMapWebinar} disabled={mapping || !selectedWebinarId}
              className="bg-navy-600 hover:bg-navy-700">
              {mapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Map Recording
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
